// Shared fetchers used by the Tranche AH detail pages. Each fetcher is
// thin — it just calls apiClient with the closest available route and
// returns the entries (or empty array on permission errors so a
// non-master can still load tenant-scoped detail pages without
// the side rail blowing up).
//
// API gaps flagged for follow-up live as TODOs at the call sites in the
// detail page components — collected here so future tranches know
// what's missing without grepping every page.
import { apiClient, ApiError, type ApiRequestInit } from './api.js';
import type { AuditEntry, OutboxJob } from '@ng-unifi/ui';
import {
  type ApiAuditRow,
  isDashboardNoise,
  mapAuditRowToEntry,
} from './auditLabels.js';

interface ApiOutboxRow {
  id: string;
  type: string;
  state: OutboxJob['state'];
  attempt_count: number;
  max_attempts?: number | null;
  next_run_at?: string | null;
  /** Server returns the most recent attempt timestamp under various names
   * depending on the row state; we surface whichever is set. */
  last_attempt_at?: string | null;
  created_at: string;
  resource_type: string;
  resource_id: string;
}

const swallowForbidden = (err: unknown): { data: [] } => {
  if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
    return { data: [] };
  }
  throw err;
};

function mapAuditRow(row: ApiAuditRow): AuditEntry {
  return mapAuditRowToEntry(row);
}

/** Pull the most recent N audit rows for the current scope. */
export async function fetchRecentAudit(limit = 10): Promise<AuditEntry[]> {
  const res = await apiClient
    .get<{ data: ApiAuditRow[] }>(`/api/v1/audit?limit=${limit}`)
    .catch(swallowForbidden);
  return res.data.filter((row) => !isDashboardNoise(row.action)).map(mapAuditRow);
}

/** Pull audit rows scoped to one resource via server-side filtering. */
export async function fetchAuditForResource(
  resourceType: string,
  resourceId: string,
  limit = 10,
  scopeOpts?: ApiRequestInit,
): Promise<AuditEntry[]> {
  const qs = new URLSearchParams({
    resource_type: resourceType,
    resource_id: resourceId,
    limit: String(limit),
  });
  const res = await apiClient
    .get<{ data: ApiAuditRow[] }>(`/api/v1/audit?${qs.toString()}`, scopeOpts)
    .catch(swallowForbidden);
  return res.data.filter((row) => !isDashboardNoise(row.action)).map(mapAuditRow);
}

/**
 * Pull recent outbox jobs filtered by resource. The list endpoint
 * supports `resource_type` and `resource_id` so this scopes cleanly
 * to the entity in focus.
 */
export async function fetchOutboxJobsForResource(
  resourceType: string,
  resourceId: string,
  limit = 10,
  scopeOpts?: ApiRequestInit,
): Promise<OutboxJob[]> {
  const qs = new URLSearchParams({
    resource_type: resourceType,
    resource_id: resourceId,
    limit: String(limit),
  });
  const res = await apiClient
    .get<{ data: ApiOutboxRow[] }>(`/api/v1/admin/outbox/jobs?${qs.toString()}`, scopeOpts)
    .catch(swallowForbidden);
  return res.data.map((row) => ({
    id: row.id,
    kind: row.type,
    state: row.state,
    attempt_count: row.attempt_count,
    max_attempts: row.max_attempts ?? null,
    last_attempted_at: row.last_attempt_at ?? row.next_run_at ?? null,
    href: `/outbox?resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`,
  }));
}

/**
 * Pull outbox jobs scoped to a tenant (no resource filter). Used by
 * TenantDetailPage to surface failures across the slice.
 *
 */
export async function fetchOutboxJobsForTenant(
  tenantId: string,
  limit = 10,
  scopeOpts?: ApiRequestInit,
): Promise<OutboxJob[]> {
  const qs = new URLSearchParams({
    limit: String(limit),
    state: 'failed',
    tenant_id: tenantId,
  });
  const res = await apiClient
    .get<{ data: (ApiOutboxRow & { tenant_id?: string })[] }>(
      `/api/v1/admin/outbox/jobs?${qs.toString()}`,
      scopeOpts,
    )
    .catch(swallowForbidden);
  return res.data.map((row) => ({
      id: row.id,
      kind: row.type,
      state: row.state,
      attempt_count: row.attempt_count,
      max_attempts: row.max_attempts ?? null,
      last_attempted_at: row.last_attempt_at ?? row.next_run_at ?? null,
      href: null,
    }));
}
